/**
 * GSC Links Management Page
 * 
 * Comprehensive links dashboard with 5 tabs:
 * - External Links: Backlinks from other domains
 * - Linking Domains: Domain-level aggregations
 * - Top Linked Pages: Pages with most backlinks
 * - Link Changes: Gains/losses tracking with charts
 * - Internal Links: Site structure analysis
 */

import { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Grid,
  Tabs,
  Tab,
  Button,
  Chip,
  TextField,
  MenuItem,
  Alert,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Paper,
  Link as MuiLink,
  Tooltip,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from '@mui/material';
import {
  OpenInNew as OpenIcon,
  Link as LinkIcon,
  TrendingUp as TrendingUpIcon,
  TrendingDown as TrendingDownIcon,
  Public as PublicIcon,
  Refresh as RefreshIcon,
  ArrowBack as ArrowBackIcon,
} from '@mui/icons-material';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { LineChart, Line, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip as RechartsTooltip, Legend, ResponsiveContainer } from 'recharts';
import {
  getExternalLinks,
  getExternalLinksSummary,
  getLinkingDomains,
  getTopLinkedPages,
  getLinkChanges,
  getDailyChangeCounts,
  getInternalLinks,
  getInternalLinksSummary,
  getInternalOrphanPages,
  getInternalSourcePages,
  getInternalTargetPages,
} from '@/services/customer';
import type {
  ExternalLinksResponse,
  ExternalLinksSummaryDTO,
  LinkingDomainsResponse,
  TopLinkedPagesResponse,
  LinkChangesResponse,
  DailyChangeCountsDTO,
  InternalLinksResponse,
  InternalLinksSummaryDTO,
  InternalOrphanPagesResponse,
  InternalSourcePagesResponse,
  InternalTargetPagesResponse,
} from '@/types/customer';
import { useSiteStore } from '@/store/site.store';

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;
  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`links-tabpanel-${index}`}
      aria-labelledby={`links-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ py: 3 }}>{children}</Box>}
    </div>
  );
}

export default function CustomerLinksManagementPage() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [activeTab, setActiveTab] = useState(0);
  const { selectedSite } = useSiteStore();
  const siteId = selectedSite?.id;
  
  // External Links state
  const [externalLinks, setExternalLinks] = useState<ExternalLinksResponse | null>(null);
  const [externalSummary, setExternalSummary] = useState<ExternalLinksSummaryDTO | null>(null);
  const [externalLoading, setExternalLoading] = useState(false);
  const [externalPage, setExternalPage] = useState(0);
  const [externalPageSize, setExternalPageSize] = useState(20);
  const [sourceDomainFilter, setSourceDomainFilter] = useState('');
  
  // Linking Domains state
  const [linkingDomains, setLinkingDomains] = useState<LinkingDomainsResponse | null>(null);
  const [domainsLoading, setDomainsLoading] = useState(false);
  const [domainsPage, setDomainsPage] = useState(0);
  const [domainsPageSize, setDomainsPageSize] = useState(20);
  const [minAuthority, setMinAuthority] = useState<number | undefined>(undefined);
  const [maxSpam, setMaxSpam] = useState<number | undefined>(undefined);
  
  // Top Linked Pages state
  const [topPages, setTopPages] = useState<TopLinkedPagesResponse | null>(null);
  const [topPagesLoading, setTopPagesLoading] = useState(false);
  const [topPagesPage, setTopPagesPage] = useState(0);
  const [topPagesPageSize, setTopPagesPageSize] = useState(20);
  
  // Link Changes state
  const [linkChanges, setLinkChanges] = useState<LinkChangesResponse | null>(null);
  const [dailyChanges, setDailyChanges] = useState<DailyChangeCountsDTO | null>(null);
  const [changesLoading, setChangesLoading] = useState(false);
  const [changesPage, setChangesPage] = useState(0);
  const [changesPageSize, setChangesPageSize] = useState(20);
  const [changeTypeFilter, setChangeTypeFilter] = useState<'GAINED' | 'LOST' | ''>('');
  const [changeDateRange, setChangeDateRange] = useState(30); // last 30 days
  
  // Internal Links state
  const [internalLinks, setInternalLinks] = useState<InternalLinksResponse | null>(null);
  const [internalSummary, setInternalSummary] = useState<InternalLinksSummaryDTO | null>(null);
  const [internalOrphans, setInternalOrphans] = useState<InternalOrphanPagesResponse | null>(null);
  const [internalSourcePages, setInternalSourcePages] = useState<InternalSourcePagesResponse | null>(null);
  const [internalTargetPages, setInternalTargetPages] = useState<InternalTargetPagesResponse | null>(null);
  const [internalLoading, setInternalLoading] = useState(false);
  const [internalPage, setInternalPage] = useState(0);
  const [internalPageSize, setInternalPageSize] = useState(20);
  const [internalSourcePage, setInternalSourcePage] = useState(0);
  const [internalSourcePageSize, setInternalSourcePageSize] = useState(10);
  const [internalTargetPage, setInternalTargetPage] = useState(0);
  const [internalTargetPageSize, setInternalTargetPageSize] = useState(10);
  const [breakdownOpen, setBreakdownOpen] = useState(false);
  const [breakdownSourcePage, setBreakdownSourcePage] = useState<string | null>(null);
  const [breakdownTargetPage, setBreakdownTargetPage] = useState<string | null>(null);
  const [breakdownLinks, setBreakdownLinks] = useState<InternalLinksResponse | null>(null);
  const [breakdownLoading, setBreakdownLoading] = useState(false);
  const [breakdownPage, setBreakdownPage] = useState(0);
  const [breakdownPageSize, setBreakdownPageSize] = useState(20);
  
  const [error, setError] = useState<string | null>(null);

  // Load data based on active tab
  useEffect(() => {
    loadTabData();
  }, [
    activeTab,
    externalPage,
    domainsPage,
    topPagesPage,
    changesPage,
    internalPage,
    internalSourcePage,
    internalTargetPage,
    siteId,
  ]);

  useEffect(() => {
    if (!breakdownOpen) return;
    if (!breakdownSourcePage && !breakdownTargetPage) return;
    loadBreakdownLinks();
  }, [breakdownOpen, breakdownSourcePage, breakdownTargetPage, breakdownPage, breakdownPageSize]);

  const loadTabData = async () => {
    setError(null);
    if (!siteId) {
      setError('Please select a site to view links data.');
      return;
    }
    try {
      switch (activeTab) {
        case 0: // External Links
          await loadExternalLinks();
          break;
        case 1: // Linking Domains
          await loadLinkingDomains();
          break;
        case 2: // Top Linked Pages
          await loadTopLinkedPages();
          break;
        case 3: // Link Changes
          await loadLinkChanges();
          break;
        case 4: // Internal Links
          await loadInternalLinks();
          break;
      }
    } catch (err: any) {
      setError(err.message || 'Failed to load data');
    }
  };

  const loadExternalLinks = async () => {
    setExternalLoading(true);
    try {
      const [linksData, summaryData] = await Promise.all([
        getExternalLinks(externalPage, externalPageSize, sourceDomainFilter || undefined),
        getExternalLinksSummary(),
      ]);
      setExternalLinks(linksData);
      setExternalSummary(summaryData);
    } finally {
      setExternalLoading(false);
    }
  };

  const loadLinkingDomains = async () => {
    setDomainsLoading(true);
    try {
      const data = await getLinkingDomains(domainsPage, domainsPageSize, minAuthority, maxSpam);
      setLinkingDomains(data);
    } finally {
      setDomainsLoading(false);
    }
  };

  const loadTopLinkedPages = async () => {
    setTopPagesLoading(true);
    try {
      const data = await getTopLinkedPages(topPagesPage, topPagesPageSize);
      setTopPages(data);
    } finally {
      setTopPagesLoading(false);
    }
  };

  const loadLinkChanges = async () => {
    setChangesLoading(true);
    try {
      const endDate = new Date();
      const startDate = new Date();
      startDate.setDate(startDate.getDate() - changeDateRange);
      
      const [changesData, dailyData] = await Promise.all([
        getLinkChanges(
          startDate.toISOString().split('T')[0],
          endDate.toISOString().split('T')[0],
          changesPage,
          changesPageSize,
          changeTypeFilter || undefined
        ),
        getDailyChangeCounts(
          startDate.toISOString().split('T')[0],
          endDate.toISOString().split('T')[0]
        ),
      ]);
      setLinkChanges(changesData);
      setDailyChanges(dailyData);
    } finally {
      setChangesLoading(false);
    }
  };

  const loadInternalLinks = async () => {
    setInternalLoading(true);
    try {
      const [linksData, summaryData, orphansData, sourcePagesData, targetPagesData] = await Promise.all([
        getInternalLinks(internalPage, internalPageSize),
        getInternalLinksSummary(),
        getInternalOrphanPages(),
        getInternalSourcePages(internalSourcePage, internalSourcePageSize),
        getInternalTargetPages(internalTargetPage, internalTargetPageSize),
      ]);
      setInternalLinks(linksData);
      setInternalSummary(summaryData);
      setInternalOrphans(orphansData);
      setInternalSourcePages(sourcePagesData);
      setInternalTargetPages(targetPagesData);
    } finally {
      setInternalLoading(false);
    }
  };

  const loadBreakdownLinks = async () => {
    setBreakdownLoading(true);
    try {
      const data = await getInternalLinks(
        breakdownPage,
        breakdownPageSize,
        breakdownSourcePage || undefined,
        breakdownTargetPage || undefined
      );
      setBreakdownLinks(data);
    } finally {
      setBreakdownLoading(false);
    }
  };

  const handleTabChange = (_event: React.SyntheticEvent, newValue: number) => {
    setActiveTab(newValue);
    setSearchParams({ tab: newValue.toString() });
  };

  const formatNumber = (num: number) => {
    return new Intl.NumberFormat().format(num);
  };

  const formatDate = (dateStr: string) => {
    return new Date(dateStr).toLocaleDateString();
  };

  // Transform daily changes data for chart
  const chartData = dailyChanges?.dailyCounts.reduce((acc, item) => {
    const existing = acc.find(d => d.date === item.date);
    if (existing) {
      existing[item.changeType.toLowerCase()] = item.count;
    } else {
      acc.push({
        date: formatDate(item.date),
        [item.changeType.toLowerCase()]: item.count,
      });
    }
    return acc;
  }, [] as any[]) || [];

  return (
    <Box sx={{ maxWidth: { xs: 400, sm: 768, md: 900, lg: 1200 }, mx: 'auto', px: { xs: 2, sm: 3, md: 4 }, py: 3 }}>
      {/* Header */}
      <Box sx={{ mb: 3 }}>
        <Typography variant="h4" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <LinkIcon />
          GSC Links Management
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Monitor external backlinks, linking domains, internal site structure, and link changes over time
        </Typography>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      {/* Tabs */}
      <Paper sx={{ mb: 3 }}>
        <Tabs value={activeTab} onChange={handleTabChange} variant="scrollable" scrollButtons="auto">
          <Tab label="External Links" />
          <Tab label="Linking Domains" />
          <Tab label="Top Linked Pages" />
          <Tab label="Link Changes" />
          <Tab label="Internal Links" />
        </Tabs>
      </Paper>

      {/* Tab 0: External Links */}
      <TabPanel value={activeTab} index={0}>
        {externalSummary && (
          <Grid container spacing={2} sx={{ mb: 3 }}>
            <Grid item xs={12} sm={6} md={2.4}>
              <Card>
                <CardContent>
                  <Typography variant="body2" color="text.secondary">Total Backlinks</Typography>
                  <Typography variant="h5">{formatNumber(externalSummary.totalBacklinks)}</Typography>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} sm={6} md={2.4}>
              <Card>
                <CardContent>
                  <Typography variant="body2" color="text.secondary">Linking Domains</Typography>
                  <Typography variant="h5">{formatNumber(externalSummary.uniqueLinkingDomains)}</Typography>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} sm={6} md={2.4}>
              <Card>
                <CardContent>
                  <Typography variant="body2" color="text.secondary">Target Pages</Typography>
                  <Typography variant="h5">{formatNumber(externalSummary.uniqueTargetPages)}</Typography>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} sm={6} md={2.4}>
              <Card>
                <CardContent>
                  <Typography variant="body2" color="text.secondary">Follow Links</Typography>
                  <Typography variant="h5" color="success.main">{formatNumber(externalSummary.followLinks)}</Typography>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} sm={6} md={2.4}>
              <Card>
                <CardContent>
                  <Typography variant="body2" color="text.secondary">NoFollow Links</Typography>
                  <Typography variant="h5" color="text.secondary">{formatNumber(externalSummary.nofollowLinks)}</Typography>
                </CardContent>
              </Card>
            </Grid>
          </Grid>
        )}

        <Card>
          <CardContent>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
              <Typography variant="h6">External Backlinks</Typography>
              <Box sx={{ display: 'flex', gap: 2 }}>
                <TextField
                  size="small"
                  label="Filter by Domain"
                  value={sourceDomainFilter}
                  onChange={(e) => setSourceDomainFilter(e.target.value)}
                  sx={{ width: 250 }}
                />
                <IconButton onClick={loadExternalLinks} disabled={externalLoading}>
                  <RefreshIcon />
                </IconButton>
              </Box>
            </Box>

            {externalLoading ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                <CircularProgress />
              </Box>
            ) : (
              <>
                <TableContainer>
                  <Table>
                    <TableHead>
                      <TableRow>
                        <TableCell>Source Domain</TableCell>
                        <TableCell>Source URL</TableCell>
                        <TableCell>Target URL</TableCell>
                        <TableCell>Link Text</TableCell>
                        <TableCell>Type</TableCell>
                        <TableCell>Discovered</TableCell>
                        <TableCell>Last Seen</TableCell>
                        <TableCell>Status</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {externalLinks?.links.map((link) => (
                        <TableRow key={link.id}>
                          <TableCell>
                            <Tooltip title={link.sourceDomain}>
                              <Typography variant="body2" noWrap sx={{ maxWidth: 150 }}>
                                {link.sourceDomain}
                              </Typography>
                            </Tooltip>
                          </TableCell>
                          <TableCell>
                            <MuiLink href={link.sourceUrl} target="_blank" rel="noopener" sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                              <Typography variant="body2" noWrap sx={{ maxWidth: 200 }}>
                                {link.sourceUrl}
                              </Typography>
                              <OpenIcon fontSize="small" />
                            </MuiLink>
                          </TableCell>
                          <TableCell>
                            <Tooltip title={link.targetUrl}>
                              <Typography variant="body2" noWrap sx={{ maxWidth: 200 }}>
                                {link.targetUrl}
                              </Typography>
                            </Tooltip>
                          </TableCell>
                          <TableCell>
                            <Tooltip title={link.linkText || 'No text'}>
                              <Typography variant="body2" noWrap sx={{ maxWidth: 150 }}>
                                {link.linkText || '-'}
                              </Typography>
                            </Tooltip>
                          </TableCell>
                          <TableCell>
                            <Chip 
                              label={link.linkType} 
                              size="small" 
                              color={link.linkType === 'FOLLOW' ? 'success' : 'default'}
                            />
                          </TableCell>
                          <TableCell>{formatDate(link.discoveredDate)}</TableCell>
                          <TableCell>{formatDate(link.lastSeenDate)}</TableCell>
                          <TableCell>
                            <Chip 
                              label={link.isActive ? 'Active' : 'Inactive'} 
                              size="small" 
                              color={link.isActive ? 'success' : 'default'}
                            />
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
                <TablePagination
                  component="div"
                  count={externalLinks?.totalCount || 0}
                  page={externalPage}
                  onPageChange={(_e, page) => setExternalPage(page)}
                  rowsPerPage={externalPageSize}
                  onRowsPerPageChange={(e) => {
                    setExternalPageSize(parseInt(e.target.value, 10));
                    setExternalPage(0);
                  }}
                />
              </>
            )}
          </CardContent>
        </Card>
      </TabPanel>

      {/* Tab 1: Linking Domains */}
      <TabPanel value={activeTab} index={1}>
        <Card>
          <CardContent>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
              <Typography variant="h6">Linking Domains</Typography>
              <Box sx={{ display: 'flex', gap: 2 }}>
                <TextField
                  size="small"
                  label="Min Authority"
                  type="number"
                  value={minAuthority || ''}
                  onChange={(e) => setMinAuthority(e.target.value ? parseInt(e.target.value) : undefined)}
                  sx={{ width: 150 }}
                />
                <TextField
                  size="small"
                  label="Max Spam Score"
                  type="number"
                  value={maxSpam || ''}
                  onChange={(e) => setMaxSpam(e.target.value ? parseInt(e.target.value) : undefined)}
                  sx={{ width: 150 }}
                />
                <IconButton onClick={loadLinkingDomains} disabled={domainsLoading}>
                  <RefreshIcon />
                </IconButton>
              </Box>
            </Box>

            {domainsLoading ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                <CircularProgress />
              </Box>
            ) : (
              <>
                <TableContainer>
                  <Table>
                    <TableHead>
                      <TableRow>
                        <TableCell>Domain</TableCell>
                        <TableCell align="right">Total Links</TableCell>
                        <TableCell align="right">Target Pages</TableCell>
                        <TableCell align="right">Follow</TableCell>
                        <TableCell align="right">NoFollow</TableCell>
                        <TableCell align="right">Authority</TableCell>
                        <TableCell align="right">Spam Score</TableCell>
                        <TableCell>First Seen</TableCell>
                        <TableCell>Last Seen</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {linkingDomains?.domains.map((domain) => (
                        <TableRow key={domain.id}>
                          <TableCell>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                              <PublicIcon fontSize="small" color="action" />
                              <Typography variant="body2">{domain.sourceDomain}</Typography>
                            </Box>
                          </TableCell>
                          <TableCell align="right"><strong>{formatNumber(domain.totalLinks)}</strong></TableCell>
                          <TableCell align="right">{formatNumber(domain.totalTargetPages)}</TableCell>
                          <TableCell align="right">
                            <Chip label={formatNumber(domain.followLinks)} size="small" color="success" />
                          </TableCell>
                          <TableCell align="right">
                            <Chip label={formatNumber(domain.nofollowLinks)} size="small" />
                          </TableCell>
                          <TableCell align="right">
                            {domain.domainAuthority ? (
                              <Chip 
                                label={domain.domainAuthority} 
                                size="small" 
                                color={domain.domainAuthority >= 50 ? 'success' : 'default'}
                              />
                            ) : '-'}
                          </TableCell>
                          <TableCell align="right">
                            {domain.spamScore !== undefined ? (
                              <Chip 
                                label={domain.spamScore} 
                                size="small" 
                                color={domain.spamScore <= 30 ? 'success' : domain.spamScore <= 60 ? 'warning' : 'error'}
                              />
                            ) : '-'}
                          </TableCell>
                          <TableCell>{formatDate(domain.firstSeenDate)}</TableCell>
                          <TableCell>{formatDate(domain.lastSeenDate)}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
                <TablePagination
                  component="div"
                  count={linkingDomains?.totalCount || 0}
                  page={domainsPage}
                  onPageChange={(_e, page) => setDomainsPage(page)}
                  rowsPerPage={domainsPageSize}
                  onRowsPerPageChange={(e) => {
                    setDomainsPageSize(parseInt(e.target.value, 10));
                    setDomainsPage(0);
                  }}
                />
              </>
            )}
          </CardContent>
        </Card>
      </TabPanel>

      {/* Tab 2: Top Linked Pages */}
      <TabPanel value={activeTab} index={2}>
        <Card>
          <CardContent>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
              <Typography variant="h6">Top Linked Pages</Typography>
              <IconButton onClick={loadTopLinkedPages} disabled={topPagesLoading}>
                <RefreshIcon />
              </IconButton>
            </Box>

            {topPagesLoading ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                <CircularProgress />
              </Box>
            ) : (
              <>
                <TableContainer>
                  <Table>
                    <TableHead>
                      <TableRow>
                        <TableCell>Rank</TableCell>
                        <TableCell>Target URL</TableCell>
                        <TableCell align="right">Total Links</TableCell>
                        <TableCell align="right">Unique Source Pages</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {topPages?.pages.map((page, index) => (
                        <TableRow key={index}>
                          <TableCell>
                            <Chip 
                              label={`#${topPagesPage * topPagesPageSize + index + 1}`} 
                              size="small" 
                              color={index < 3 ? 'primary' : 'default'}
                            />
                          </TableCell>
                          <TableCell>
                            <MuiLink href={page.targetUrl} target="_blank" rel="noopener" sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                              <Typography variant="body2" noWrap sx={{ maxWidth: 500 }}>
                                {page.targetUrl}
                              </Typography>
                              <OpenIcon fontSize="small" />
                            </MuiLink>
                          </TableCell>
                          <TableCell align="right">
                            <Typography variant="h6" color="primary">
                              {formatNumber(page.totalLinks ?? page.backlinksCount)}
                            </Typography>
                          </TableCell>
                          <TableCell align="right">
                            <Typography variant="body2">
                              {formatNumber(page.uniqueSourcePages ?? 0)}
                            </Typography>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
                <TablePagination
                  component="div"
                  count={topPages?.totalCount || 0}
                  page={topPagesPage}
                  onPageChange={(_e, page) => setTopPagesPage(page)}
                  rowsPerPage={topPagesPageSize}
                  onRowsPerPageChange={(e) => {
                    setTopPagesPageSize(parseInt(e.target.value, 10));
                    setTopPagesPage(0);
                  }}
                />
              </>
            )}
          </CardContent>
        </Card>
      </TabPanel>

      {/* Tab 3: Link Changes */}
      <TabPanel value={activeTab} index={3}>
        {linkChanges && (
          <Grid container spacing={2} sx={{ mb: 3 }}>
            <Grid item xs={12} sm={6}>
              <Card>
                <CardContent>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <TrendingUpIcon color="success" />
                    <Box>
                      <Typography variant="body2" color="text.secondary">Links Gained</Typography>
                      <Typography variant="h4" color="success.main">{formatNumber(linkChanges.gainedCount)}</Typography>
                    </Box>
                  </Box>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} sm={6}>
              <Card>
                <CardContent>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <TrendingDownIcon color="error" />
                    <Box>
                      <Typography variant="body2" color="text.secondary">Links Lost</Typography>
                      <Typography variant="h4" color="error.main">{formatNumber(linkChanges.lostCount)}</Typography>
                    </Box>
                  </Box>
                </CardContent>
              </Card>
            </Grid>
          </Grid>
        )}

        {/* Time Series Chart */}
        {dailyChanges && chartData.length > 0 && (
          <Card sx={{ mb: 3 }}>
            <CardContent>
              <Typography variant="h6" gutterBottom>Daily Link Changes</Typography>
              <ResponsiveContainer width="100%" height={300}>
                <LineChart data={chartData}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="date" />
                  <YAxis />
                  <RechartsTooltip />
                  <Legend />
                  <Line type="monotone" dataKey="gained" stroke="#4caf50" name="Gained" />
                  <Line type="monotone" dataKey="lost" stroke="#f44336" name="Lost" />
                </LineChart>
              </ResponsiveContainer>
            </CardContent>
          </Card>
        )}

        <Card>
          <CardContent>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
              <Typography variant="h6">Link Changes History</Typography>
              <Box sx={{ display: 'flex', gap: 2 }}>
                <TextField
                  select
                  size="small"
                  label="Change Type"
                  value={changeTypeFilter}
                  onChange={(e) => setChangeTypeFilter(e.target.value as any)}
                  sx={{ width: 150 }}
                >
                  <MenuItem value="">All</MenuItem>
                  <MenuItem value="GAINED">Gained</MenuItem>
                  <MenuItem value="LOST">Lost</MenuItem>
                </TextField>
                <TextField
                  select
                  size="small"
                  label="Date Range"
                  value={changeDateRange}
                  onChange={(e) => setChangeDateRange(parseInt(e.target.value))}
                  sx={{ width: 150 }}
                >
                  <MenuItem value={7}>Last 7 days</MenuItem>
                  <MenuItem value={30}>Last 30 days</MenuItem>
                  <MenuItem value={90}>Last 90 days</MenuItem>
                </TextField>
                <IconButton onClick={loadLinkChanges} disabled={changesLoading}>
                  <RefreshIcon />
                </IconButton>
              </Box>
            </Box>

            {changesLoading ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                <CircularProgress />
              </Box>
            ) : (
              <>
                <TableContainer>
                  <Table>
                    <TableHead>
                      <TableRow>
                        <TableCell>Change Type</TableCell>
                        <TableCell>Link Type</TableCell>
                        <TableCell>Source Domain</TableCell>
                        <TableCell>Target URL</TableCell>
                        <TableCell>Change Date</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {linkChanges?.changes.map((change) => (
                        <TableRow key={change.id}>
                          <TableCell>
                            <Chip 
                              label={change.changeType} 
                              size="small" 
                              color={change.changeType === 'GAINED' ? 'success' : 'error'}
                              icon={change.changeType === 'GAINED' ? <TrendingUpIcon /> : <TrendingDownIcon />}
                            />
                          </TableCell>
                          <TableCell>
                            <Chip label={change.linkType} size="small" variant="outlined" />
                          </TableCell>
                          <TableCell>{change.sourceDomain || '-'}</TableCell>
                          <TableCell>
                            <Tooltip title={change.targetUrl}>
                              <Typography variant="body2" noWrap sx={{ maxWidth: 300 }}>
                                {change.targetUrl}
                              </Typography>
                            </Tooltip>
                          </TableCell>
                          <TableCell>{formatDate(change.changeDate)}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
                <TablePagination
                  component="div"
                  count={linkChanges?.totalCount || 0}
                  page={changesPage}
                  onPageChange={(_e, page) => setChangesPage(page)}
                  rowsPerPage={changesPageSize}
                  onRowsPerPageChange={(e) => {
                    setChangesPageSize(parseInt(e.target.value, 10));
                    setChangesPage(0);
                  }}
                />
              </>
            )}
          </CardContent>
        </Card>
      </TabPanel>

      {/* Tab 4: Internal Links */}
      <TabPanel value={activeTab} index={4}>
        {internalSummary && (
          <Grid container spacing={2} sx={{ mb: 3 }}>
            <Grid item xs={12} sm={6} md={3}>
              <Card>
                <CardContent>
                  <Typography variant="body2" color="text.secondary">Total Internal Links</Typography>
                  <Typography variant="h5">{formatNumber(internalSummary.totalInternalLinks)}</Typography>
                  <Typography variant="caption" color="text.secondary">
                    Snapshot: {formatDate(internalSummary.snapshotDate)}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <Card>
                <CardContent>
                  <Typography variant="body2" color="text.secondary">Source Pages</Typography>
                  <Typography variant="h5">{formatNumber(internalSummary.uniqueSourcePages)}</Typography>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <Card>
                <CardContent>
                  <Typography variant="body2" color="text.secondary">Target Pages</Typography>
                  <Typography variant="h5">{formatNumber(internalSummary.uniqueTargetPages)}</Typography>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <Card>
                <CardContent>
                  <Typography variant="body2" color="text.secondary">Orphan Pages</Typography>
                  <Typography variant="h5" color={internalSummary.orphanPagesCount > 0 ? 'warning.main' : 'success.main'}>
                    {formatNumber(internalSummary.orphanPagesCount)}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
          </Grid>
        )}

        {internalSourcePages && (
          <Card sx={{ mb: 3 }}>
            <CardContent>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                <Typography variant="h6">Source Pages (Outbound Links)</Typography>
                <IconButton onClick={loadInternalLinks} disabled={internalLoading}>
                  <RefreshIcon />
                </IconButton>
              </Box>
              <TableContainer>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Source Page</TableCell>
                      <TableCell align="right">Total Links</TableCell>
                      <TableCell align="right">Unique Targets</TableCell>
                      <TableCell align="right">Breakdown</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {internalSourcePages.pages.map((page) => (
                      <TableRow key={page.sourcePage}>
                        <TableCell>
                          <MuiLink href={page.sourcePage} target="_blank" rel="noopener">
                            <Typography variant="body2" noWrap sx={{ maxWidth: 520 }}>
                              {page.sourcePage}
                            </Typography>
                          </MuiLink>
                        </TableCell>
                        <TableCell align="right">{formatNumber(page.totalOutboundLinks)}</TableCell>
                        <TableCell align="right">{formatNumber(page.uniqueTargetPages)}</TableCell>
                        <TableCell align="right">
                          <Button
                            size="small"
                            variant={breakdownSourcePage === page.sourcePage ? 'contained' : 'outlined'}
                            onClick={() => {
                              setBreakdownSourcePage(page.sourcePage);
                              setBreakdownTargetPage(null);
                              setBreakdownPage(0);
                              setBreakdownOpen(true);
                            }}
                          >
                            View Links
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
              <TablePagination
                component="div"
                count={internalSourcePages.totalCount}
                page={internalSourcePage}
                onPageChange={(_e, page) => setInternalSourcePage(page)}
                rowsPerPage={internalSourcePageSize}
                onRowsPerPageChange={(e) => {
                  setInternalSourcePageSize(parseInt(e.target.value, 10));
                  setInternalSourcePage(0);
                }}
              />
            </CardContent>
          </Card>
        )}

        {internalTargetPages && (
          <Card sx={{ mb: 3 }}>
            <CardContent>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                <Typography variant="h6">Target Pages (Inbound Links)</Typography>
                <IconButton onClick={loadInternalLinks} disabled={internalLoading}>
                  <RefreshIcon />
                </IconButton>
              </Box>
              <TableContainer>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Target Page</TableCell>
                      <TableCell align="right">Total Links</TableCell>
                      <TableCell align="right">Unique Sources</TableCell>
                      <TableCell align="right">Breakdown</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {internalTargetPages.pages.map((page) => (
                      <TableRow key={page.targetPage}>
                        <TableCell>
                          <MuiLink href={page.targetPage} target="_blank" rel="noopener">
                            <Typography variant="body2" noWrap sx={{ maxWidth: 520 }}>
                              {page.targetPage}
                            </Typography>
                          </MuiLink>
                        </TableCell>
                        <TableCell align="right">{formatNumber(page.totalInboundLinks)}</TableCell>
                        <TableCell align="right">{formatNumber(page.uniqueSourcePages)}</TableCell>
                        <TableCell align="right">
                          <Button
                            size="small"
                            variant={breakdownTargetPage === page.targetPage ? 'contained' : 'outlined'}
                            onClick={() => {
                              setBreakdownTargetPage(page.targetPage);
                              setBreakdownSourcePage(null);
                              setBreakdownPage(0);
                              setBreakdownOpen(true);
                            }}
                          >
                            View Sources
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
              <TablePagination
                component="div"
                count={internalTargetPages.totalCount}
                page={internalTargetPage}
                onPageChange={(_e, page) => setInternalTargetPage(page)}
                rowsPerPage={internalTargetPageSize}
                onRowsPerPageChange={(e) => {
                  setInternalTargetPageSize(parseInt(e.target.value, 10));
                  setInternalTargetPage(0);
                }}
              />
            </CardContent>
          </Card>
        )}

        {internalOrphans && internalOrphans.orphanPagesCount > 0 && (
          <Card sx={{ mb: 3 }}>
            <CardContent>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                <Typography variant="h6">Orphan Pages (No Inbound Links)</Typography>
                <Chip
                  label={formatNumber(internalOrphans.orphanPagesCount)}
                  color="warning"
                  size="small"
                />
              </Box>
              <TableContainer>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Page URL</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {internalOrphans.orphanPages.map((pageUrl) => (
                      <TableRow key={pageUrl}>
                        <TableCell>
                          <MuiLink href={pageUrl} target="_blank" rel="noopener">
                            <Typography variant="body2" noWrap sx={{ maxWidth: 600 }}>
                              {pageUrl}
                            </Typography>
                          </MuiLink>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </CardContent>
          </Card>
        )}

        <Card>
          <CardContent>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
              <Typography variant="h6">Internal Site Structure</Typography>
              <IconButton onClick={loadInternalLinks} disabled={internalLoading}>
                <RefreshIcon />
              </IconButton>
            </Box>

            {internalLoading ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                <CircularProgress />
              </Box>
            ) : (
              <>
                <TableContainer sx={{ maxHeight: 520 }}>
                  <Table>
                    <TableHead>
                      <TableRow>
                        <TableCell sx={{ minWidth: 280 }}>Source Page</TableCell>
                        <TableCell sx={{ minWidth: 280 }}>Target Page</TableCell>
                        <TableCell sx={{ minWidth: 180 }}>Link Text</TableCell>
                        <TableCell align="right">Link Count</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {internalLinks?.links.map((link) => (
                        <TableRow key={link.id}>
                          <TableCell>
                            <Tooltip title={link.sourcePage}>
                              <MuiLink href={link.sourcePage} target="_blank" rel="noopener">
                                <Typography variant="body2" sx={{ wordBreak: 'break-all' }}>
                                  {link.sourcePage}
                                </Typography>
                              </MuiLink>
                            </Tooltip>
                          </TableCell>
                          <TableCell>
                            <Tooltip title={link.targetPage}>
                              <MuiLink href={link.targetPage} target="_blank" rel="noopener">
                                <Typography variant="body2" sx={{ wordBreak: 'break-all' }}>
                                  {link.targetPage}
                                </Typography>
                              </MuiLink>
                            </Tooltip>
                          </TableCell>
                          <TableCell>
                            <Tooltip title={link.linkText || 'No text'}>
                              <Typography variant="body2" sx={{ wordBreak: 'break-word' }}>
                                {link.linkText || '-'}
                              </Typography>
                            </Tooltip>
                          </TableCell>
                          <TableCell align="right">
                            <Chip label={link.linkCount} size="small" color={link.linkCount > 1 ? 'primary' : 'default'} />
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
                <TablePagination
                  component="div"
                  count={internalLinks?.totalCount || 0}
                  page={internalPage}
                  onPageChange={(_e, page) => setInternalPage(page)}
                  rowsPerPage={internalPageSize}
                  onRowsPerPageChange={(e) => {
                    setInternalPageSize(parseInt(e.target.value, 10));
                    setInternalPage(0);
                  }}
                />
              </>
            )}
          </CardContent>
        </Card>

        <Dialog
          open={breakdownOpen}
          onClose={() => setBreakdownOpen(false)}
          fullWidth
          maxWidth="lg"
        >
          <DialogTitle>
            {breakdownSourcePage
              ? `Outbound links from ${breakdownSourcePage}`
              : `Inbound links to ${breakdownTargetPage}`}
          </DialogTitle>
          <DialogContent dividers>
            {breakdownLoading ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                <CircularProgress />
              </Box>
            ) : (
              <>
                <TableContainer sx={{ maxHeight: 520 }}>
                  <Table>
                    <TableHead>
                      <TableRow>
                        {breakdownSourcePage ? (
                          <TableCell sx={{ minWidth: 280 }}>Target Page</TableCell>
                        ) : (
                          <TableCell sx={{ minWidth: 280 }}>Source Page</TableCell>
                        )}
                        <TableCell sx={{ minWidth: 180 }}>Link Text</TableCell>
                        <TableCell align="right">Link Count</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {breakdownLinks?.links.map((link) => (
                        <TableRow key={link.id}>
                          {breakdownSourcePage ? (
                            <TableCell>
                              <Tooltip title={link.targetPage}>
                                <MuiLink href={link.targetPage} target="_blank" rel="noopener">
                                  <Typography variant="body2" sx={{ wordBreak: 'break-all' }}>
                                    {link.targetPage}
                                  </Typography>
                                </MuiLink>
                              </Tooltip>
                            </TableCell>
                          ) : (
                            <TableCell>
                              <Tooltip title={link.sourcePage}>
                                <MuiLink href={link.sourcePage} target="_blank" rel="noopener">
                                  <Typography variant="body2" sx={{ wordBreak: 'break-all' }}>
                                    {link.sourcePage}
                                  </Typography>
                                </MuiLink>
                              </Tooltip>
                            </TableCell>
                          )}
                          <TableCell>
                            <Tooltip title={link.linkText || 'No text'}>
                              <Typography variant="body2" sx={{ wordBreak: 'break-word' }}>
                                {link.linkText || '-'}
                              </Typography>
                            </Tooltip>
                          </TableCell>
                          <TableCell align="right">
                            <Chip label={link.linkCount} size="small" color={link.linkCount > 1 ? 'primary' : 'default'} />
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
                <TablePagination
                  component="div"
                  count={breakdownLinks?.totalCount || 0}
                  page={breakdownPage}
                  onPageChange={(_e, page) => setBreakdownPage(page)}
                  rowsPerPage={breakdownPageSize}
                  onRowsPerPageChange={(e) => {
                    setBreakdownPageSize(parseInt(e.target.value, 10));
                    setBreakdownPage(0);
                  }}
                />
              </>
            )}
          </DialogContent>
          <DialogActions>
            <Button
              variant="outlined"
              onClick={() => {
                setBreakdownOpen(false);
                setBreakdownSourcePage(null);
                setBreakdownTargetPage(null);
              }}
            >
              Close
            </Button>
          </DialogActions>
        </Dialog>
      </TabPanel>
    </Box>
  );
}
